import { forwardRef, useState, useCallback, ReactNode } from 'react';
import { IconButton, Button } from '@material-ui/core';
import { CustomContentProps, useSnackbar } from 'notistack';
import CloseIcon from '@material-ui/icons/Close';

import { StyledMaterialDesignContent } from './StyledMaterialDesignContent';
import { DetailsDialog } from './DetailsDialog';

interface CustomSnackbarContentProps extends CustomContentProps {
  details?: object | string;
  hideCloseButton?: boolean;
}

export const CustomSnackbarContent = forwardRef<
  HTMLDivElement,
  CustomSnackbarContentProps
>((props, ref) => {
  const { closeSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const { details, hideCloseButton } = props;

  const handleOpenDetails = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    closeSnackbar(props.id);
  }, [closeSnackbar, props.id]);

  const externalAction =
    typeof props.action === 'function'
      ? props.action(props.id)
      : (props.action as ReactNode | undefined);

  const detailsAction =
    externalAction ??
    (details ? (
      <Button
        variant="text"
        size="small"
        style={{
          color: '#fff',
          borderColor: '#fff',
          borderRadius: 120,
          textTransform: 'none'
        }}
        onClick={handleOpenDetails}>
        Details
      </Button>
    ) : undefined);

  // Render the close button inside the content's `action` slot so that the
  // forwarded ref stays on the snackbar's root node — notistack needs that node
  // to run the exit transition, otherwise closeSnackbar can't remove the item.
  const resolvedAction =
    !detailsAction && hideCloseButton ? undefined : (
      <>
        {detailsAction}
        {!hideCloseButton && (
          <IconButton size="small" onClick={() => closeSnackbar(props.id)}>
            <CloseIcon style={{ color: '#fff', fontSize: 20 }} />
          </IconButton>
        )}
      </>
    );

  return (
    <>
      <StyledMaterialDesignContent
        ref={ref}
        {...props}
        action={resolvedAction}
      />
      <DetailsDialog open={open} onClose={handleClose} details={details} />
    </>
  );
});
